<template>
    <div class="orders-wrapper">
        <div class="orders-header">
            <div>
                <h1>{{ resolvedPageTitle }}</h1>
                <p class="subtitle">{{ resolvedPageSubtitle }}</p>
            </div>

            <div class="orders-meta" v-if="pagination.total > 0">
                <span class="pill pill-soft">
                    {{ $tc("labels.modules.orderingTable.orders.count", pagination.total, { count: pagination.total }) }}
                </span>
            </div>
        </div>

        <div class="orders-filters card">
            <div class="card-body py-3">
                <div class="row align-items-start">
                    <div class="col-md-2 col-sm-6 mb-3 mb-md-0">
                        <label class="form-label filter-label">
                            {{ $tl("labels.modules.orderingTable.orders.filters.date") }}
                        </label>
                        <datepicker
                            class="orders-calendar"
                            :class="{ 'calendar-loading': calendarLoading }"
                            :language="datePickerLocale"
                            :monday-first="true"
                            :highlighted="{ dates: highlightedDates }"
                            :value="filters.date"
                            @input="onDateChanged"
                            @changedMonth="onMonthChanged"
                            input-class="form-control"
                            calendar-class="orders-calendar-panel" />
                        <div class="d-flex mt-2">
                            <button type="button" class="btn btn-sm btn-outline-secondary me-2" @click="setToday">
                                {{ $tl("labels.modules.orderingTable.orders.filters.today") }}
                            </button>
                            <button v-if="filters.date" type="button" class="btn btn-sm btn-outline-secondary" @click="clearDate">
                                {{ $tl("labels.modules.orderingTable.orders.filters.clear") }}
                            </button>
                        </div>
                    </div>

                    <div class="col-md-2 col-sm-6">
                        <label class="form-label filter-label" for="orders-status-filter">
                            {{ $tl("labels.modules.orderingTable.orders.filters.status") }}
                        </label>
                        <select
                            id="orders-status-filter"
                            v-model="filters.status"
                            class="form-select align-middle d-inline-block"
                            @change="applyFilters">
                            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <!-- États -->
        <div v-if="loading" class="state state-loading">
            <div class="noshow-loader" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
            </div>
            <span>{{ $tl("labels.modules.orderingTable.orders.loading") }}</span>
        </div>

        <div v-else-if="error" class="state state-error">
            {{ error }}
        </div>

        <div v-else-if="!orders || !orders.length" class="state state-empty">
            {{ $tl("labels.modules.orderingTable.orders.noOrders") }}
        </div>

        <!-- Tableau -->
        <div v-else class="table-container">
            <table class="orders-table">
                <thead>
                    <tr>
                        <th>{{ $tl("labels.modules.orderingTable.orders.table.id") }}</th>
                        <th>{{ $tl("labels.modules.orderingTable.orders.table.channel") }}</th>
                        <th>{{ $tl("labels.modules.orderingTable.orders.table.table") }}</th>
                        <th>{{ $tl("labels.modules.orderingTable.orders.table.customer") }}</th>
                        <th>{{ $tl("labels.modules.orderingTable.orders.table.comment") }}</th>
                        <th>
                            {{ $tl("labels.modules.orderingTable.orders.table.items") }}
                        </th>
                        <th class="sortable" @click="toggleSort('total')">
                            {{ $tl("labels.modules.orderingTable.orders.table.total") }}
                            <span class="sort-indicator">{{ sortArrow("total") }}</span>
                        </th>
                        <th>{{ $tl("labels.modules.orderingTable.orders.table.status") }}</th>
                        <th class="sortable" @click="toggleSort('created_at')">
                            {{ $tl("labels.modules.orderingTable.orders.table.created") }}
                            <span class="sort-indicator">{{ sortArrow("created_at") }}</span>
                        </th>
                        <th class="col-actions">{{ $tl("labels.modules.orderingTable.orders.table.actions") }}</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="order in orders" :key="order.id">
                        <td class="col-id">
                            <div class="order-identifiers">
                                <span class="id-badge">#{{ order.id }}</span>
                                <span v-if="getOrderNumber(order)" class="order-number-badge">
                                    {{
                                        $tl("labels.modules.orderingTable.orders.number", restaurantId, {
                                            number: getOrderNumber(order),
                                        })
                                    }}
                                </span>
                            </div>
                        </td>

                        <td class="col-channel">
                            <span class="pill pill-channel">
                                {{ order.channel || "table" }}
                            </span>
                        </td>

                        <td class="col-table">
                            <span class="table-label">
                                {{ getTable(order) }}
                            </span>
                        </td>

                        <td class="col-customer">
                            {{ order.customer_name || "—" }}
                            <div v-if="order.beeper_number != null" class="small">Beeper / chevalet : {{ order.beeper_number }}</div>
                        </td>

                        <td class="col-comment" :title="order.customer_comment || ''">
                            {{ order.customer_comment || "—" }}
                        </td>

                        <td class="col-items">
                            {{ itemCount(order) }}
                        </td>

                        <td class="col-total">
                            {{ formatTotal(order) }}
                        </td>

                        <td class="col-status">
                            <span :class="['status-pill', 'status-' + (order.status || 'unknown')]">
                                {{ humanStatus(order.status) }}
                            </span>
                        </td>

                        <td class="col-date">
                            {{ formatDate(order.created_at) }}
                        </td>

                        <td class="col-actions">
                            <button type="button" class="ghost-button" @click="showOrder(order)">
                                {{ $tl("labels.detail") }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="pagination.total > 0" class="orders-pagination">
            <Pagination
                :nbPages="pagination.totalPages"
                :nbResults="pagination.total"
                :nbResultsPerPage="pagination.perPage"
                :currentPage="pagination.currentPage"
                :pageSizeOptions="pageSizeOptions"
                :includeAll="false"
                @set-current-page="setCurrentPage"
                @set-nb-results-per-page="setPerPage" />
        </div>
        <modal v-if="canShowCommand" @close="closeFromModal">
            <h5 slot="header">
                {{
                    $tl("labels.modules.orderingTable.orders.details.title", restaurantId, {
                        id: getOrderNumber(currentOrder) || currentOrder.id,
                    })
                }}
            </h5>
            <div slot="body">
                <!-- Produits de démonstration -->
                <div
                    v-if="currentOrder.has_demo_products"
                    class="alert alert-warning small d-flex align-items-start position-relative alerte-position">
                    <i class="fa fa-exclamation-triangle me-2 mt-1"></i>
                    <div>
                        <strong>{{ $tl("labels.modules.orderingTable.orders.demo.title") }}</strong>
                        <div>{{ $tl("labels.modules.orderingTable.orders.demo.message") }}</div>
                    </div>
                </div>

                <!-- Infos intégration POS si erreur réelle -->
                <div
                    v-if="currentOrder.integration_error_message && currentOrder.integration_error_message !== 'from-demo'"
                    class="alert alert-danger small d-flex align-items-start position-relative alerte-position">
                    <i class="fa fa-exclamation-circle me-2 mt-1"></i>
                    <div>
                        <strong>{{ $tl("labels.modules.orderingTable.orders.integration.note") }} :</strong>
                        <div class="integration-error-message">{{ currentOrder.integration_error_message }}</div>
                    </div>
                </div>

                <!-- Infos générales -->
                <div class="card shadow-sm border-0 mb-3">
                    <div class="card-body py-3 px-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div class="text-muted text-uppercase small mb-1">{{ $tl("labels.modules.orderingTable.orders.details.table") }}</div>
                                <div class="d-flex align-items-center">
                                    <i class="fa fa-chair me-2 text-secondary"></i>
                                    <span class="fw-bold">
                                        {{ getTable(currentOrder) }}
                                    </span>
                                    <span class="text-muted small ms-2">
                                        ({{ getSeat(currentOrder) }} {{ $tl("labels.modules.orderingTable.orders.details.seats") }})
                                    </span>
                                </div>
                            </div>

                            <div class="text-end">
                                <div class="text-muted text-uppercase small mb-1">
                                    {{ $tl("labels.modules.orderingTable.orders.details.status") }}
                                </div>
                                <span class="badge" :class="statusBadgeClass(currentOrder.status)">
                                    {{ statusLabel(currentOrder.status) }}
                                </span>
                            </div>
                        </div>

                        <hr class="my-3" />

                        <div class="mb-3">
                            <div class="text-muted text-uppercase small mb-1">
                                {{ $tl("labels.modules.orderingTable.orders.details.reference") }}
                            </div>
                            <code class="small">{{ currentOrder.reference || "—" }}</code>
                        </div>

                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div class="text-muted text-uppercase small mb-1">
                                    {{ $tl("labels.modules.orderingTable.orders.details.channel") }}
                                </div>
                                <span class="badge text-bg-info text-uppercase">
                                    {{ currentOrder.channel }}
                                </span>
                            </div>
                            <div class="text-end">
                                <div class="text-muted text-uppercase small mb-1">
                                    {{ $tl("labels.modules.orderingTable.orders.details.createdAt") }}
                                </div>
                                <div class="small">
                                    <i class="fa fa-clock-o me-1 text-secondary"></i>
                                    {{ formatDate(currentOrder.created_at) }}
                                </div>
                            </div>
                        </div>

                        <div v-if="currentOrder.customer_name || currentOrder.customer_comment || currentOrder.beeper_number != null" class="mt-3">
                            <div v-if="currentOrder.beeper_number != null">Beeper / chevalet : {{ currentOrder.beeper_number }}</div>
                            <div class="text-muted text-uppercase small mb-1">Client</div>
                            <div class="fw-bold mb-2">
                                {{ currentOrder.customer_name || "—" }}
                            </div>
                            <div v-if="currentOrder.customer_comment">
                                <div class="text-muted text-uppercase small mb-1">Commentaire</div>
                                <div class="small">{{ currentOrder.customer_comment }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Articles -->
                <div v-if="detailLoading" class="state state-loading mb-3">
                    <div class="noshow-loader" aria-hidden="true">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <span>{{ $tl("labels.modules.orderingTable.orders.loading") }}</span>
                </div>
                <div v-else-if="detailError" class="state state-error mb-3">
                    {{ detailError }}
                </div>
                <div class="card shadow-sm border-0 mb-3">
                    <div class="card-header py-2 px-3 bg-light d-flex justify-content-between align-items-center">
                        <span class="small text-uppercase text-muted">
                            {{ $tl("labels.modules.orderingTable.orders.items.title") }} ({{ itemCount(currentOrder) }})
                        </span>
                        <span class="small text-muted">
                            {{ $tl("labels.modules.orderingTable.orders.summary.total") }} :
                            <strong>{{ formatMoney(currentOrder.total_cents, currentOrder.currency) }}</strong>
                        </span>
                    </div>
                    <div class="items-table-wrapper">
                        <table class="items-table">
                            <thead>
                                <tr>
                                    <th>{{ $tl("labels.modules.orderingTable.orders.items.article") }}</th>
                                    <th class="text-center">{{ $tl("labels.modules.orderingTable.orders.items.quantity") }}</th>
                                    <th class="text-end">{{ $tl("labels.modules.orderingTable.orders.items.unitPrice") }}</th>
                                    <th class="text-end">{{ $tl("labels.modules.orderingTable.orders.items.total") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in currentOrder.items" :key="item.id">
                                    <td class="item-name">
                                        <div>{{ item.name_snapshot }}</div>
                                        <ul v-if="item.options && item.options.length" class="ps-3 mb-0">
                                            <li v-for="option in rootOptions(item.options)" :key="option.id">
                                                {{ formatOption(option, currentOrder.currency) }}
                                                <ul v-if="childOptions(item.options, option.id).length" class="ps-3 mb-0">
                                                    <li v-for="child in childOptions(item.options, option.id)" :key="child.id">
                                                        {{ formatOption(child, currentOrder.currency) }}
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                        <small v-else class="text-muted"> Aucune option </small>
                                    </td>
                                    <td class="text-center item-quantity">x{{ item.quantity }}</td>
                                    <td class="text-end item-price">{{ formatMoney(item.unit_price_cents_snapshot, currentOrder.currency) }}</td>
                                    <td class="text-end item-total fw-bold">
                                        {{ formatMoney(item.line_total_cents_snapshot, currentOrder.currency) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="card-footer py-2 px-3">
                        <div class="d-flex justify-content-end">
                            <div class="text-end small">
                                <!-- TODO: sous-total / remise / taxes masqués temporairement, à réactiver plus tard -->
                                <div style="display: none !important" class="d-flex justify-content-between">
                                    <span class="text-muted me-3">{{ $tl("labels.modules.orderingTable.orders.summary.subtotal") }}</span>
                                    <span>{{ formatMoney(currentOrder.subtotal_cents, currentOrder.currency) }}</span>
                                </div>
                                <div style="display: none !important" class="d-flex justify-content-between">
                                    <span class="text-muted me-3">{{ $tl("labels.modules.orderingTable.orders.summary.discount") }}</span>
                                    <span class="text-danger"> - {{ formatMoney(currentOrder.discount_cents, currentOrder.currency) }} </span>
                                </div>
                                <div style="display: none !important" class="d-flex justify-content-between">
                                    <span class="text-muted me-3">{{ $tl("labels.modules.orderingTable.orders.summary.tax") }}</span>
                                    <span>{{ formatMoney(currentOrder.tax_cents, currentOrder.currency) }}</span>
                                </div>
                                <hr style="display: none !important" class="my-1" />
                                <div class="d-flex justify-content-between fw-bold">
                                    <span class="me-2">{{ $tl("labels.modules.orderingTable.orders.summary.total") }}</span>
                                    <span>{{ formatMoney(currentOrder.total_cents, currentOrder.currency) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- FOOTER -->
            <div slot="footer" class="d-flex justify-content-between align-items-center">
                <small class="text-muted me-2">
                    {{ $tl("labels.modules.orderingTable.orders.details.updatedAt") }} : {{ formatDate(currentOrder.updated_at) }}
                </small>
                <button class="btn btn-outline-secondary btn-circle btn-sm" @click="closeCommand">
                    {{ $tl("labels.modules.orderingTable.orders.details.close") }}
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { fr } from "vuejs-datepicker/dist/locale";
import Pagination from "../../../components/Datatable/Pagination.vue";
import orderingTableOrderNumber from "../../../utils/orderingTableOrderNumber.mjs";

export default {
    props: {
        pageTitle: {
            type: String,
            default: null,
        },
        pageSubtitle: {
            type: String,
            default: null,
        },
        statusOptionsOverride: {
            type: Array,
            default: null,
        },
        defaultStatus: {
            type: String,
            default: "all",
        },
    },
    components: {
        Datepicker,
        Pagination,
    },
    data() {
        return {
            orders: [],
            loading: true,
            error: null,
            canShowCommand: false,
            currentOrder: {},
            detailLoading: false,
            detailError: null,
            detailRequest: null,
            filters: {
                date: null,
                status: this.defaultStatus || "all",
            },
            calendarMonth: null,
            calendarDays: [],
            calendarLoading: false,
            pagination: {
                currentPage: 1,
                perPage: 10,
                total: 0,
                totalPages: 1,
            },
            pageSizeOptions: [10, 20, 50],
            sort: {
                column: null,
                direction: "asc",
            },
            datePickerLocale: fr,
            socket: {
                channel: null,
                refreshTimer: null,
                refreshTimeout: null,
            },
            syncIntervalMs: 60000,
        };
    },
    computed: {
        restaurantId() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        resolvedPageTitle() {
            return this.pageTitle || this.$tl("labels.modules.orderingTable.orders.title");
        },
        resolvedPageSubtitle() {
            return this.pageSubtitle || this.$tl("labels.modules.orderingTable.orders.subtitle");
        },
        highlightedDates() {
            if (!this.calendarDays || !this.calendarDays.length) return [];
            return this.calendarDays.map((day) => new Date(`${day}T00:00:00`)).filter((date) => !Number.isNaN(date.getTime()));
        },
        statusOptions() {
            if (Array.isArray(this.statusOptionsOverride) && this.statusOptionsOverride.length > 0) {
                return this.statusOptionsOverride;
            }

            return [
                { value: "all", label: this.$tl("labels.modules.orderingTable.orders.filters.all") },
                { value: "created", label: this.$tl("labels.modules.orderingTable.orders.status.created") },
                { value: "validated", label: this.$tl("labels.modules.orderingTable.orders.status.validated") },
                { value: "preparing", label: this.$tl("labels.modules.orderingTable.orders.status.preparing") },
                { value: "ready", label: this.$tl("labels.modules.orderingTable.orders.status.ready") },
                { value: "served", label: this.$tl("labels.modules.orderingTable.orders.status.served") },
                { value: "paid", label: this.$tl("labels.modules.orderingTable.orders.status.paid") },
                { value: "canceled", label: this.$tl("labels.modules.orderingTable.orders.status.canceled") },
                { value: "failed", label: this.$tl("labels.modules.orderingTable.orders.status.failed") },
            ];
        },
    },
    methods: {
        showOrder(order) {
            this.detailRequest = null;
            const hasItems = Array.isArray(order?.items);
            this.currentOrder = {
                ...(order || {}),
                items: Array.isArray(order?.items) ? order.items : [],
            };
            this.canShowCommand = true;
            this.detailLoading = false;
            this.detailError = null;
            if (!hasItems) {
                this.fetchOrderDetails(order?.id);
            }
        },
        closeCommand() {
            this.detailRequest = null;
            this.canShowCommand = false;
            this.currentOrder = null;
            this.detailLoading = false;
            this.detailError = null;
        },
        closeFromModal(e) {
            if (!e.target.classList.contains("modal-wrapper")) {
                e.stopPropagation();
            } else {
                this.closeCommand();
            }
        },

        formatMoney(cents, currency) {
            if (cents == null) return "-";
            return (cents / 100).toLocaleString("fr-FR", {
                style: "currency",
                currency: currency || "EUR",
            });
        },
        formatOption(option, currency) {
            const name = option.name_snapshot || option.name || "Option";
            const qty = option.quantity && option.quantity > 0 ? ` x${option.quantity}` : "";
            const cents = option.total_cents_snapshot != null ? option.total_cents_snapshot : option.price_delta_cents_snapshot;
            const amount = Number(cents);
            const sign = amount > 0 ? "+" : "";
            const price = cents != null && amount !== 0 ? ` (${sign}${this.formatMoney(cents, currency)})` : "";
            return `${name}${qty}${price}`;
        },
        rootOptions(options) {
            if (!Array.isArray(options)) return [];

            const ids = new Set(options.map((option) => Number(option.id)));
            return options.filter((option) => {
                const parentId = Number(option.parent_order_item_option_id);
                return !option.parent_order_item_option_id || !ids.has(parentId);
            });
        },
        childOptions(options, parentId) {
            if (!Array.isArray(options)) return [];

            return options.filter((option) => Number(option.parent_order_item_option_id) === Number(parentId));
        },
        statusBadgeClass(status) {
            switch (status) {
                case "created":
                    return "text-bg-warning";
                case "validated":
                    return "text-bg-success";
                case "preparing":
                    return "text-bg-warning";
                case "ready":
                    return "text-bg-primary";
                case "served":
                    return "text-bg-secondary";
                case "paid":
                    return "text-bg-info";
                case "canceled":
                    return "text-bg-danger";
                case "failed":
                    return "text-bg-danger";
                default:
                    return "text-bg-secondary";
            }
        },
        statusLabel(status) {
            switch (status) {
                case "created":
                    return "Créée";
                case "validated":
                    return "Validée";
                case "preparing":
                    return "En préparation";
                case "ready":
                    return "Prête";
                case "served":
                    return "Servie";
                case "paid":
                    return "Payée";
                case "canceled":
                    return "Annulée";
                case "failed":
                    return "Échouée";
                default:
                    return status || "Inconnu";
            }
        },
        getOrderNumber: orderingTableOrderNumber,
        fetchOrders({ silent = false } = {}) {
            if (!silent) {
                this.loading = true;
                this.error = null;
            }

            const calendarMonth = this.resolveCalendarMonth();
            if (!this.calendarMonth) {
                this.calendarMonth = calendarMonth;
            }

            const params = new URLSearchParams();
            params.set("page", this.pagination.currentPage);
            params.set("per_page", this.pagination.perPage);
            params.set("include_items", "0");
            params.set("include_items_count", "1");

            if (this.filters.status) {
                params.set("status", this.filters.status);
            }

            if (this.filters.date instanceof Date) {
                const dateValue = this.formatDateOnly(this.filters.date);
                params.set("date", dateValue);
            }

            if (calendarMonth) {
                params.set("calendar_month", calendarMonth);
            }

            if (this.sort.column) {
                params.set("sort_by", this.sort.column);
                params.set("sort_dir", this.sort.direction);
            }

            this.httpGet(`/api2/v1/${this.restaurantId}/ordering-table/orders?${params.toString()}`)
                .then((response) => {
                    const payload = response?.data || {};
                    this.orders = Array.isArray(payload.data) ? payload.data : [];

                    const meta = payload.meta || {};
                    const pagination = meta.pagination || {};
                    const keepAll = this.pagination.perPage === "all";
                    this.pagination.currentPage = pagination.current_page || 1;
                    if (!keepAll) {
                        this.pagination.perPage = pagination.per_page || this.pagination.perPage;
                    }
                    this.pagination.total = pagination.total || 0;
                    this.pagination.totalPages = pagination.total_pages || 1;

                    const calendar = meta.calendar || {};
                    this.calendarDays = Array.isArray(calendar.days) ? calendar.days : [];
                    if (calendar.month) {
                        this.calendarMonth = calendar.month;
                    }
                })
                .finally(() => {
                    if (!silent) {
                        this.loading = false;
                    }
                });
        },
        fetchOrderDetails(orderId) {
            if (!orderId || !this.restaurantId) return;

            this.detailLoading = true;
            this.detailError = null;
            this.detailRequest = { updates: {} };
            const request = this.detailRequest;
            const isCurrentRequest = () => this.detailRequest === request && this.canShowCommand && this.currentOrder?.id === orderId;

            this.httpGet(`/api2/v1/${this.restaurantId}/ordering-table/orders/${orderId}`)
                .then((response) => {
                    const payload = response?.data?.data || response?.data || null;
                    if (payload && isCurrentRequest()) {
                        const order = { ...this.currentOrder, ...payload, ...request.updates };
                        this.currentOrder = {
                            ...order,
                            items: Array.isArray(order.items) ? order.items : [],
                        };
                    }
                })
                .catch(() => {
                    if (isCurrentRequest()) {
                        this.detailError = "Impossible de charger les détails.";
                    }
                })
                .finally(() => {
                    if (isCurrentRequest()) {
                        this.detailLoading = false;
                        this.detailRequest = null;
                    }
                });
        },
        fetchCalendarIndicators(month) {
            if (!this.restaurantId) return;

            const calendarMonth = month || this.resolveCalendarMonth();
            this.calendarMonth = calendarMonth;
            this.calendarLoading = true;

            const params = new URLSearchParams();
            params.set("page", 1);
            params.set("per_page", 1);
            params.set("include_items", "0");

            if (this.filters.status) {
                params.set("status", this.filters.status);
            }

            if (calendarMonth) {
                params.set("calendar_month", calendarMonth);
            }

            this.httpGet(`/api2/v1/${this.restaurantId}/ordering-table/orders?${params.toString()}`)
                .then((response) => {
                    const calendar = response?.data?.meta?.calendar || {};
                    this.calendarDays = Array.isArray(calendar.days) ? calendar.days : [];
                    if (calendar.month) {
                        this.calendarMonth = calendar.month;
                    }
                })
                .catch(() => {})
                .finally(() => {
                    this.calendarLoading = false;
                });
        },
        applyFilters() {
            this.pagination.currentPage = 1;
            this.fetchOrders();
        },
        setToday() {
            this.filters.date = new Date();
            this.calendarMonth = this.formatMonth(this.filters.date);
            this.pagination.currentPage = 1;
            this.fetchOrders();
        },
        clearDate() {
            this.filters.date = null;
            this.pagination.currentPage = 1;
            this.fetchOrders();
        },
        onDateChanged(date) {
            this.filters.date = date || null;
            if (date instanceof Date) {
                this.calendarMonth = this.formatMonth(date);
            }
            this.pagination.currentPage = 1;
            this.fetchOrders();
        },
        onMonthChanged(value) {
            let monthDate = null;
            if (value instanceof Date) {
                monthDate = value;
            } else if (value && typeof value === "object" && value.timestamp) {
                const parsed = new Date(value.timestamp);
                if (!Number.isNaN(parsed.getTime())) {
                    monthDate = parsed;
                }
            } else if (typeof value === "string" || typeof value === "number") {
                const parsed = new Date(value);
                if (!Number.isNaN(parsed.getTime())) {
                    monthDate = parsed;
                }
            }

            if (!monthDate) return;

            this.fetchCalendarIndicators(this.formatMonth(monthDate));
        },
        setCurrentPage(page) {
            this.pagination.currentPage = page;
            this.fetchOrders();
        },
        setPerPage(perPage) {
            if (perPage === "all") {
                this.pagination.perPage = "all";
            } else {
                const parsed = Number.parseInt(perPage, 10);
                if (Number.isFinite(parsed) && parsed > 0) {
                    this.pagination.perPage = parsed;
                }
            }
            this.pagination.currentPage = 1;
            this.fetchOrders();
        },
        toggleSort(column) {
            if (this.sort.column !== column) {
                this.sort.column = column;
                this.sort.direction = "asc";
            } else if (this.sort.direction === "asc") {
                this.sort.direction = "desc";
            } else {
                this.sort.column = null;
                this.sort.direction = "asc";
            }
            this.pagination.currentPage = 1;
            this.fetchOrders();
        },
        sortArrow(column) {
            if (this.sort.column !== column) return "";
            return this.sort.direction === "asc" ? "▲" : "▼";
        },
        formatDateOnly(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        },
        formatMonth(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            return `${year}-${month}`;
        },
        resolveCalendarMonth() {
            if (this.calendarMonth) return this.calendarMonth;
            if (this.filters.date instanceof Date) {
                return this.formatMonth(this.filters.date);
            }
            return this.formatMonth(new Date());
        },
        itemCount(order) {
            if (order.items_sum_quantity != null) {
                return Number(order.items_sum_quantity);
            }
            if (order.items && Array.isArray(order.items)) {
                return order.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
            }
            if (order.itemsCount != null) {
                return order.itemsCount;
            }
            if (order.items_count != null) {
                return order.items_count;
            }
            return 0;
        },

        formatTotal(order) {
            const cents = order.total_cents || 0;
            return (cents / 100).toFixed(2) + " " + (order.currency || "EUR");
        },

        humanStatus(status) {
            return (
                {
                    created: "Créée",
                    validated: "Validée",
                    preparing: "En préparation",
                    ready: "Prête",
                    served: "Servie",
                    paid: "Payée",
                    canceled: "Annulée",
                    failed: "Échouée",
                }[status] ||
                status ||
                "—"
            );
        },

        formatDate(date) {
            if (!date) return "—";
            return new Date(date).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        getTable(order) {
            if (order === null) return "--";
            if (order && order.tables) {
                return order.tables.name;
            }
            return order.table_label_snapshot || "--";
        },

        getSeat(order) {
            if (order && order.tables) {
                return order.tables.seats;
            }
            return "--";
        },
        registerSockets() {
            if (!window.Echo || !this.restaurantId) return;

            const channelName = `App.restaurant.${this.restaurantId}.ordering-table.orders`;
            if (this.socket.channel === channelName) {
                return;
            }

            Echo.private(channelName)
                .listen(".ordering_table.order.created", this.onOrderCreated)
                .listen(".ordering_table.order.updated", this.onOrderUpdated);

            this.socket.channel = channelName;
            this.$store.commit("sockets/addChannel", channelName);
        },
        unregisterSockets() {
            if (this.socket.channel) {
                Echo.leave(this.socket.channel);
                this.$store.commit("sockets/removeChannel", this.socket.channel);
                this.socket.channel = null;
            }
        },
        startBackgroundSync() {
            this.stopBackgroundSync();
            this.socket.refreshTimer = setInterval(() => {
                if (!this.loading) {
                    this.fetchOrders({ silent: true });
                }
            }, this.syncIntervalMs);
        },
        stopBackgroundSync() {
            if (this.socket.refreshTimer) {
                clearInterval(this.socket.refreshTimer);
                this.socket.refreshTimer = null;
            }
            if (this.socket.refreshTimeout) {
                clearTimeout(this.socket.refreshTimeout);
                this.socket.refreshTimeout = null;
            }
        },
        scheduleRefresh() {
            if (this.socket.refreshTimeout) return;
            this.socket.refreshTimeout = setTimeout(() => {
                this.socket.refreshTimeout = null;
                this.fetchOrders({ silent: true });
            }, 1500);
        },
        orderMatchesFilters(order) {
            if (!order) return false;

            if (this.filters.status && this.filters.status !== "all") {
                if (order.status !== this.filters.status) {
                    return false;
                }
            }

            if (this.filters.date instanceof Date) {
                const filterDate = this.formatDateOnly(this.filters.date);
                const orderDate = order.created_at ? String(order.created_at).slice(0, 10) : null;
                if (orderDate !== filterDate) {
                    return false;
                }
            }

            return true;
        },
        updatePaginationTotals(delta) {
            if (!Number.isFinite(delta) || delta === 0) return;

            const nextTotal = Math.max(0, (this.pagination.total || 0) + delta);
            this.pagination.total = nextTotal;

            const perPage = this.pagination.perPage === "all" ? 0 : Number(this.pagination.perPage);
            if (perPage > 0) {
                this.pagination.totalPages = Math.max(1, Math.ceil(nextTotal / perPage));
                if (this.pagination.currentPage > this.pagination.totalPages) {
                    this.pagination.currentPage = this.pagination.totalPages;
                }
            } else {
                this.pagination.totalPages = nextTotal > 0 ? 1 : 0;
            }
        },
        upsertOrder(order, { prepend = false } = {}) {
            const index = this.orders.findIndex((item) => item.id === order.id);
            if (index !== -1) {
                this.$set(this.orders, index, order);
                return false;
            }

            if (this.pagination.currentPage !== 1 && this.pagination.perPage !== "all") {
                return false;
            }

            if (prepend) {
                this.orders.unshift(order);
            } else {
                this.orders.push(order);
            }

            const perPage = this.pagination.perPage === "all" ? 0 : Number(this.pagination.perPage);
            if (perPage > 0 && this.orders.length > perPage) {
                this.orders.pop();
            }
            return true;
        },
        sortOrders() {
            if (this.sort.column !== null) {
                this.scheduleRefresh();
                return;
            }
            if (!this.orders || this.orders.length < 2) return;
            this.orders = [...this.orders].sort((a, b) => {
                const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
                const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
                return bDate - aDate;
            });

            const perPage = this.pagination.perPage === "all" ? 0 : Number(this.pagination.perPage);
            if (perPage > 0 && this.orders.length > perPage) {
                this.orders = this.orders.slice(0, perPage);
            }
        },
        updateCalendarDays(order) {
            if (!order || !order.created_at || !this.calendarMonth) return;

            const orderDay = String(order.created_at).slice(0, 10);
            if (!orderDay.startsWith(this.calendarMonth)) return;

            if (this.filters.status && this.filters.status !== "all") {
                if (order.status !== this.filters.status) {
                    return;
                }
            }

            if (!this.calendarDays.includes(orderDay)) {
                this.calendarDays.push(orderDay);
            }
        },
        onOrderCreated(payload) {
            const order = payload?.order;
            if (!order || !order.id) {
                this.scheduleRefresh();
                return;
            }

            if (this.sort.column !== null) {
                this.scheduleRefresh();
                this.updateCalendarDays(order);
                return;
            }

            if (this.orderMatchesFilters(order)) {
                const index = this.orders.findIndex((item) => item.id === order.id);
                if (index === -1) {
                    const inserted = this.upsertOrder(order, { prepend: true });
                    if (inserted) {
                        this.sortOrders();
                    }
                    this.updatePaginationTotals(1);
                } else {
                    this.$set(this.orders, index, order);
                }
            }

            this.updateCalendarDays(order);
        },
        onOrderUpdated(payload) {
            const order = payload?.order;
            if (!order || !order.id) {
                this.scheduleRefresh();
                return;
            }

            if (this.canShowCommand && this.currentOrder?.id === order.id) {
                this.currentOrder = { ...this.currentOrder, ...order };
                if (this.detailRequest) {
                    this.detailRequest.updates = { ...this.detailRequest.updates, ...order };
                }
            }

            if (this.sort.column !== null) {
                this.scheduleRefresh();
                return;
            }

            const matches = this.orderMatchesFilters(order);
            const index = this.orders.findIndex((item) => item.id === order.id);

            if (matches) {
                if (index !== -1) {
                    this.$set(this.orders, index, order);
                } else {
                    const inserted = this.upsertOrder(order, { prepend: true });
                    if (inserted) {
                        this.sortOrders();
                    }
                    this.updatePaginationTotals(1);
                }
            } else if (index !== -1) {
                this.orders.splice(index, 1);
                this.updatePaginationTotals(-1);
                this.scheduleRefresh();
            }

            if (this.filters.status && this.filters.status !== "all") {
                this.scheduleRefresh();
            }
        },
    },

    mounted() {
        this.filters.date = new Date();
        this.calendarMonth = this.formatMonth(this.filters.date);
        this.fetchOrders();
        this.registerSockets();
        this.startBackgroundSync();
    },
    beforeDestroy() {
        this.unregisterSockets();
        this.stopBackgroundSync();
    },
};
</script>

<style scoped>
.integration-error-message {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.orders-wrapper {
    padding: 20px 24px;
    background: #f5f5f7;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
    font-family:
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
}

.orders-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.orders-header h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: #111827;
}

.subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #6b7280;
}

.orders-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.orders-filters {
    margin-bottom: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
}

.orders-filters .card-body {
    padding: 16px;
}

.orders-filters .filter-label {
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    font-weight: 600;
    margin-bottom: 6px;
}

.orders-filters .form-control,
.orders-filters .form-select {
    height: 38px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
}

.orders-filters .form-control:focus,
.orders-filters .form-select:focus {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    border: 1px solid rgba(148, 163, 184, 0.4);
    color: #374151;
}

.pill-soft {
    background: #eef2ff;
}

.pill-channel {
    background: #f9fafb;
}

/* États */
.state {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #0f172a;
    padding: 10px 14px;
    border-radius: 12px;
    background: #e5e7eb;
}

.state-loading {
    background: linear-gradient(135deg, rgba(8, 62, 96, 0.08), rgba(48, 164, 183, 0.12));
    color: #083e60;
}

.state-error {
    background: #fee2e2;
    color: #b91c1c;
}

.state-empty {
    background: #f3f4f6;
}

.noshow-loader {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.noshow-loader span {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: #083e60;
    animation: noshow-bounce 0.9s infinite ease-in-out;
}

.noshow-loader span:nth-child(2) {
    background: #30a4b7;
    animation-delay: 0.15s;
}

.noshow-loader span:nth-child(3) {
    background: #083e60;
    animation-delay: 0.3s;
}

@keyframes noshow-bounce {
    0%,
    80%,
    100% {
        transform: translateY(0);
        opacity: 0.6;
    }
    40% {
        transform: translateY(-6px);
        opacity: 1;
    }
}

/* Table */
.table-container {
    margin-top: 12px;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
}

.orders-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.orders-table thead {
    background: #f9fafb;
}

.orders-table th,
.orders-table td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #eef0f4;
    white-space: nowrap;
}

.orders-table th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
    font-weight: 600;
}

.orders-table th.sortable {
    cursor: pointer;
    user-select: none;
}

.orders-table th.sortable:hover {
    color: #111827;
}

.sort-indicator {
    display: inline-block;
    margin-left: 4px;
    font-size: 10px;
    color: #4f46e5;
}

.orders-table tbody tr:hover {
    background: #f3f4ff;
}

.col-id {
    min-width: 170px;
}

.order-identifiers {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.id-badge {
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 999px;
    background: #111827;
    color: white;
    font-size: 11px;
    font-weight: 500;
}

.order-number-badge {
    display: inline-flex;
    padding: 3px 8px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    color: #374151;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.col-channel,
.col-table,
.col-customer,
.col-comment,
.col-items,
.col-total,
.col-status,
.col-date {
    max-width: 160px;
}

.col-comment {
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.orders-pagination {
    margin-top: 12px;
    padding: 6px 0;
}

.table-label {
    font-weight: 500;
    color: #111827;
}

.col-total {
    font-variant-numeric: tabular-nums;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
}

.status-created {
    background: #fef3c7;
    color: #92400e;
}

.status-validated {
    background: #dcfce7;
    color: #166534;
}

.status-paid {
    background: #dbeafe;
    color: #1d4ed8;
}

.status-canceled {
    background: #fee2e2;
    color: #b91c1c;
}

.status-unknown {
    background: #e5e7eb;
    color: #374151;
}

/*  */
.col-actions {
    text-align: right;
}

.ghost-button {
    border-radius: 999px;
    border: 1px solid #e5e7eb;
    padding: 5px 12px;
    font-size: 12px;
    background: white;
    color: #111827;
    cursor: pointer;
    transition: all 0.15s ease;
}

.ghost-button:hover {
    border-color: #4f46e5;
    color: #4f46e5;
    box-shadow: 0 0 0 1px rgba(79, 70, 229, 0.1);
}

.table-container {
    overflow-x: auto;
}

.items-table-wrapper {
    overflow-x: auto;
}

.items-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.items-table thead {
    background: #f9fafb;
}

.items-table th {
    padding: 8px 12px;
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
    font-weight: 600;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
}

.items-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
}

.items-table tbody tr:hover {
    background: #f9fafb;
}

.items-table tbody tr:last-child td {
    border-bottom: none;
}

::v-deep(.orders-calendar-panel .cell.highlighted) {
    background-color: transparent;
    position: relative;
}

::v-deep(.orders-calendar-panel .cell.highlighted::after) {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #e73b21;
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
}

::v-deep(.calendar-loading .orders-calendar-panel) {
    position: relative;
}

::v-deep(.calendar-loading .orders-calendar-panel::before) {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 12px;
    z-index: 2;
}

::v-deep(.calendar-loading .orders-calendar-panel::after) {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 22px;
    margin: -11px 0 0 -11px;
    border-radius: 50%;
    border: 3px solid #083e60;
    border-top-color: #30a4b7;
    border-right-color: #30a4b7;
    animation: noshow-spin 0.8s linear infinite;
    z-index: 3;
}

@keyframes noshow-spin {
    to {
        transform: rotate(360deg);
    }
}

.item-name {
    font-weight: 500;
    color: #111827;
    max-width: 250px;
}

.item-quantity {
    color: #6b7280;
    font-weight: 500;
    min-width: 50px;
}

.item-price {
    color: #6b7280;
    font-variant-numeric: tabular-nums;
    min-width: 80px;
}

.item-total {
    color: #111827;
    font-variant-numeric: tabular-nums;
    min-width: 90px;
}

.alerte-position {
    top: 0;
}
</style>
